import { Box, Flex, Checkbox, Text, Alert, AlertIcon } from "@chakra-ui/react";
import { Col } from "antd";
import { ContaForm } from "features/conta/typings";
import { OperacaoEnum, TipoDivisaoContaEnum } from "resources/enums/conta";
import { FormaPagamento } from "resources/enums/tenant";
import moment from "moment";
import { useEffect, useState, useMemo } from "react";
import { Control, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import Field from "components/Input/Field";
import SelectContaBancaria from "components/Input/SelectContaBancaria";
import SelectPessoas from "components/Input/SelectPessoas";
import SelectUnidade from "components/Input/SelectUnidade";
import SelectTags from "components/Input/SelectTags";
import SelectFormaPagamento from "components/Input/SelectFormaPagamento";
import AnexarArquivos from "./components/AnexarArquivos";
import { InputBaseWrapper } from "./components/InputBaseWrapper";
import Repetir from "./components/Repetir";
import RetencaoImposto from "./components/RetencaoImposto";
import SelectBase from "./components/SelectBase";
import { mapTipoEntidadeToLabel, useOptionsTipoEntidade } from "./util/helper";
import ComposicaoContaLista from "./components/ComposicaoContaLista";
import ModalComposicaoConta from "./components/ModalComposicaoConta";
import DocumentoFiscal from "./components/DocumentoFiscal";
import { TipoEntidadeEnum } from "resources/enums/conta";
import LiquidarConta from "./components/LiquidarConta";
import { ComposicaoItem } from "core/features/conta/typings";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import SelectTipoAnexoConta from "ui/components/Input/SeletctTipoAnexoConta";
import SelectPessoaFornecedora from "ui/components/Input/SelectPessoaFornecedora";

interface FormContaProps {
  operacao: OperacaoEnum;
  form: ContaForm;
  setValue: UseFormSetValue<ContaForm>;
  getValues: UseFormGetValues<ContaForm>;
  control: Control<ContaForm>;
  onChangeExcludes: (v: string) => void;
  requiredLiquidar?: boolean;
  fixedDataMovimentacao?: string;
  fixedGerarCobranca?: boolean;
  isUpdate?: boolean;
}

function FormConta({
  operacao,
  form,
  setValue,
  control,
  getValues,
  onChangeExcludes,
  requiredLiquidar,
  fixedDataMovimentacao,
  fixedGerarCobranca = false,
  isUpdate = false,
}: FormContaProps) {
  const { tenant } = useCurrentTenant();
  const unidadeId = getValues("unidadeId");
  const options = useOptionsTipoEntidade();

  const [isOpenComposicaoConta, setOpenComposicaoConta] = useState(false);

  useEffect(() => {
    const repetir = getValues("repetir");
    if (form.parcelas?.length && !repetir) {
      setValue("repetir", true);
    }
  }, [form.parcelas, getValues, setValue]);

  useEffect(() => {
    const values = getValues();

    if (form.numeroParcelas && form.numeroParcelas > 0) {
      const totalConta = values.composicoes?.reduce(
        (agg, item) => agg + (item.valor! ?? 0),
        0,
      );
      const valorParcela = totalConta
        ? Number((totalConta / form.numeroParcelas).toFixed(2))
        : 0;

      let extraValue = 0;

      const total = valorParcela * form.numeroParcelas;

      if (Number.isFinite(totalConta) && total !== totalConta) {
        extraValue = totalConta - total;
      }

      const descricaoContaOriginal = form?.parcelasExistentes?.find(
        (f) => f.id === form.grupoConta,
      )?.descricao;

      const numerosParcelas = form?.parcelasExistentes?.map(
        (f) => f.numeroParcela!,
      ) ?? [0];

      const datasVencimentoParcelasExistentes =
        form?.parcelasExistentes?.map((f) => f.dataVencimento!) ?? [];

      const ultimaDataVencimento = datasVencimentoParcelasExistentes.sort(
        (a, b) => moment(b).diff(moment(a)),
      )[0];

      const inicioNumeroParcelas = Math.max(...numerosParcelas);

      const _descricao = descricaoContaOriginal ?? values.descricao;
      setValue(
        "parcelas",
        [...Array(form.numeroParcelas).keys()].map((_, i) => ({
          dataVencimento: moment(ultimaDataVencimento ?? values.dataVencimento)
            .add(ultimaDataVencimento ? i + 1 : i, "month")
            .format("YYYY-MM-DD"),
          descricao: _descricao
            ? `${_descricao} - ${i + 1} / ${form.numeroParcelas}`
            : `Parcela ${inicioNumeroParcelas + i + 1} / ${form.numeroParcelas
            }`,
          valor:
            form.tipoDivisaoContaId ===
              TipoDivisaoContaEnum.DividirValorLiquidoEntreParcelas
              ? i === 0
                ? valorParcela + extraValue
                : valorParcela
              : totalConta || 0,
        })),
      );
    } else {
      setValue("parcelas", []);
    }
  }, [
    form.grupoConta,
    form.numeroParcelas,
    form?.parcelasExistentes,
    form.tipoDivisaoContaId,
    getValues,
    setValue,
  ]);

  const total = useMemo(() => {
    if (!form.composicoes?.length) {
      return 0;
    }

    let result = form.composicoes.reduce(
      (acc: number, next: ComposicaoItem) => {
        if (operacao === OperacaoEnum.Receita) {
          acc = acc + (next.valor || 0) * (next.operacao || 1);
        } else {
          acc = acc + (next.valor || 0);
        }

        return acc;
      },
      0,
    );

    if (form.calcularJurosAutomatico && !form.dataPagamento) {
      result +=
        (form.multa ?? 0) + (form.juros ?? 0) + (form.valorTaxaCobranca ?? 0);
    }

    return result;
  }, [
    form.calcularJurosAutomatico,
    form.composicoes,
    form.dataPagamento,
    form.juros,
    form.multa,
    form.valorTaxaCobranca,
    operacao,
  ]);

  const totalSemEncargos =
    total -
    (form.multa ?? 0) -
    (form.juros ?? 0) -
    (form.valorTaxaCobranca ?? 0);

  return (
    // @ts-ignore
    <Flex flexDir="column">
      {fixedGerarCobranca ? (
        <Alert status="warning" mt={-4} mb={4} fontSize={12}>
          <AlertIcon />
          Não é possível alterar os métodos de cobrança e a conta de recebimento
          de uma receita que foi criada com cobrança. Caso queira fazer essas
          alterações é necessário criar uma nova receita.
        </Alert>
      ) : null}
      <InputBaseWrapper gutter={16}>
        <Box as={Col} xs={12}>
          <Field
            control={control}
            rules={{ required: true }}
            name="descricao"
            label="Resumo"
          />
        </Box>

        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="dataVencimento"
            label="Vencimento"
            rules={{
              required: true,
              max: "2100-01-01",
            }}
            inputProps={{
              type: "date",
              max: "2100-01-01",
            }}
          />
        </Box>

        <Box as={Col} xs={6}>
          <Field
            control={control}
            name="dataCompetencia"
            label="Competência"
            rules={{
              max: "2100-01",
            }}
            inputProps={{
              type: "month",
              max: "2100-01",
            }}
          />
        </Box>
      </InputBaseWrapper>

      <InputBaseWrapper gutter={16}>
        <Box as={Col} xs={8}>
          <Field
            control={control}
            name="tipoEntidadeId"
            label="Entidade"
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <SelectBase
                isInvalid={isInvalid}
                onChange={(ev) => {
                  field.onChange(ev);
                  setValue("unidadeId", "");
                  setValue("pessoaId", "");
                }}
                value={field.value}
                options={options}
              />
            )}
          />
        </Box>

        {TipoEntidadeEnum.Condomino === form.tipoEntidadeId ? (
          <Box as={Col} xs={8}>
            <Field
              control={control}
              name="unidadeId"
              label={"Unidade"}
              rules={{
                required: true,
              }}
              render={({ field, isInvalid }) => (
                <SelectUnidade
                  isInvalid={isInvalid}
                  onChange={(ev) => {
                    setValue("pessoaId", "");
                    field.onChange(ev);
                  }}
                  value={field.value}
                />
              )}
            />
          </Box>
        ) : null}

        <Box
          as={Col}
          xs={TipoEntidadeEnum.Condomino === form.tipoEntidadeId ? 8 : 16}
        >
          <Field
            control={control}
            name="pessoaId"
            label={mapTipoEntidadeToLabel(form.tipoEntidadeId)}
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) =>
              form.tipoEntidadeId === TipoEntidadeEnum.Fornecedor ? (
                <SelectPessoaFornecedora
                  isInvalid={isInvalid}
                  onChange={field.onChange}
                  value={field.value}
                  unidadeId={unidadeId!}
                />
              ) : (
                <SelectPessoas
                  isInvalid={isInvalid}
                  onChange={field.onChange}
                  value={field.value}
                  unidadeId={unidadeId!}
                  tipoEntidades={
                    form.tipoEntidadeId && !form.unidadeId
                      ? [form.tipoEntidadeId]
                      : []
                  }
                />
              )
            }
          />
        </Box>
      </InputBaseWrapper>

      {operacao === OperacaoEnum.Receita ||
        [TipoEntidadeEnum.Condomino, TipoEntidadeEnum.Cliente].includes(
          form.tipoEntidadeId,
        ) ? (
        <InputBaseWrapper gutter={16}>
          <Box as={Col} offset={8} xs={8}>
            <Field
              control={control}
              name="gerarCobranca"
              render={({ field }) => (
                <Checkbox
                  disabled={fixedGerarCobranca}
                  onChange={() => field.onChange(!field.value)}
                  isChecked={!!field.value}
                >
                  <Text fontSize={12}>Gerar cobrança?</Text>
                </Checkbox>
              )}
            />
          </Box>
        </InputBaseWrapper>
      ) : null}

      <InputBaseWrapper gutter={16}>
        <Box as={Col} xs={8}>
          <Field
            shouldUnregister
            control={control}
            name="tenantContaId"
            label={
              operacao === OperacaoEnum.Despesa
                ? "Conta de Pagamento"
                : "Conta de Recebimento"
            }
            rules={{
              required: true,
            }}
            render={({ field, isInvalid }) => (
              <SelectContaBancaria
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
                markerDefault
                incluirInativas={false}
                disabled={fixedGerarCobranca}
              />
            )}
          />
        </Box>

        {/* <Box as={Col} xs={8}>
          <Field
            shouldUnregister
            control={control}
            name="centroDeCustoId"
            label={"Centro de Custo"}
            rules={{
              required: false,
            }}
            render={({ field, isInvalid }) => (
              <SelectCentroDeCusto
                isInvalid={isInvalid}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
        </Box> */}

        {operacao === OperacaoEnum.Receita && form.gerarCobranca ? (
          <Box as={Col} xs={8}>
            <Field
              control={control}
              name="formasRecebimentoIds"
              label="Métodos de cobrança"
              shouldUnregister
              rules={{
                required: true,
                minLength: 1,
              }}
              render={({ field, isInvalid }) => (
                <SelectFormaPagamento
                  disabled={fixedGerarCobranca}
                  isInvalid={isInvalid}
                  onChange={field.onChange}
                  value={field.value}
                  allowClear={false}
                  mode="multiple"
                  placeholder="Selecione..."
                  queryParams={{
                    aceitaRecebimentoOnline: true,
                    ativo: true,
                  }}
                />
              )}
            />
          </Box>
        ) : null}

        {operacao === OperacaoEnum.Despesa ? (
          <>
            <Box as={Col} xs={8}>
              <Field
                control={control}
                name="tagId"
                label="Etiqueta"
                render={({ field }) => (
                  <SelectTags
                    onChange={field.onChange}
                    value={field.value}
                    placeholder="Selecione..."
                  />
                )}
              />
            </Box>
          </>
        ) : null}
      </InputBaseWrapper>

      {tenant && operacao === OperacaoEnum.Despesa ? (
        <InputBaseWrapper gutter={16}>
          <Box as={Col} xs={8}>
            <Field
              control={control}
              name="tiposAnexosIds"
              label="Etiquetas de anexos"
              shouldUnregister
              render={({ field }) => (
                <SelectTipoAnexoConta
                  mode="multiple"
                  onChange={field.onChange}
                  value={field.value}
                  placeholder="Selecione..."
                />
              )}
            />
          </Box>

          <Box as={Col} xs={8}>
            <Field
              control={control}
              name="pessoaPagadorId"
              label={"Pagante"}
              shouldUnregister
              render={({ field, isInvalid }) => (
                <SelectPessoas
                  prependOptions={
                    tenant?.pessoaId
                      ? [
                        {
                          value: tenant.pessoaId,
                          label: tenant.nome,
                        },
                      ]
                      : []
                  }
                  isInvalid={isInvalid}
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </Box>
        </InputBaseWrapper>
      ) : null}

      {form.formaPagamentoId === FormaPagamento.Cheque ? (
        <Field
          shouldUnregister
          control={control}
          name="numeroCheque"
          label="Número do cheque"
        />
      ) : null}

      {operacao === OperacaoEnum.Receita ? (
        <InputBaseWrapper gutter={16}>
          <Box as={Col} xs={8}>
            <Field
              control={control}
              name="calcularJurosAutomatico"
              render={({ field }) => (
                <Checkbox
                  disabled={fixedGerarCobranca}
                  onChange={() => field.onChange(!field.value)}
                  isChecked={!!field.value}
                >
                  <Text fontSize={12}>Calcular juros após atraso?</Text>
                </Checkbox>
              )}
            />
          </Box>
        </InputBaseWrapper>
      ) : null}

      <ComposicaoContaLista
        operacao={operacao}
        form={form}
        control={control}
        onAdd={() => setOpenComposicaoConta(true)}
        total={total}
        getValues={getValues}
      />

      <DocumentoFiscal control={control} operacao={operacao} />

      {operacao === OperacaoEnum.Despesa ? (
        <RetencaoImposto
          control={control}
          form={form}
          setValue={setValue}
          total={total}
        />
      ) : null}

      {!form.dataPagamento ? (
        <Repetir
          operacao={operacao}
          control={control}
          form={form}
          setValue={setValue}
          title={
            isUpdate && !!form.grupoConta ? "Adicionar parcelas" : "Repetir"
          }
          disabled={isUpdate && !!form.grupoConta}
          disabledMessage={
            isUpdate && !!form.grupoConta
              ? "Não é possível adicionar parcelas a uma conta já parcelada. Para fazer essa alteração, crie uma nova conta."
              : undefined
          }
          isUpdate={isUpdate}
        />
      ) : null}

      <AnexarArquivos
        control={control}
        form={form}
        setValue={setValue}
        pushArquivoRemovido={(id) => onChangeExcludes(id)}
      />

      {!form?.dataPagamento ? (
        <LiquidarConta
          control={control}
          operacao={operacao}
          form={form}
          setValue={setValue}
          totalSemEncargos={totalSemEncargos}
          required={requiredLiquidar}
          isEdit={!!form?.id}
          fixedDataMovimentacao={fixedDataMovimentacao}
        />
      ) : null}

      {isOpenComposicaoConta ? (
        <ModalComposicaoConta
          operacao={operacao}
          isOpen={isOpenComposicaoConta}
          onClose={() => setOpenComposicaoConta(false)}
          onSubmit={(item) => {
            const items = form.composicoes;

            const newItems = items.concat(item);

            setValue("composicoes", newItems);

            setOpenComposicaoConta(false);
          }}
        />
      ) : null}
    </Flex>
  );
}

export default FormConta;
